import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export type WebhookEvent = "approval.step_approved" | "approval.completed" | "approval.rejected";

// 内置回调接收端的地址：用于联调，能自动校验签名头与事件负载格式。
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string | undefined;
export const BUILTIN_RECEIVER_URL = SUPABASE_URL
  ? `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/webhook-receiver`
  : "";

export const WEBHOOK_EVENTS: WebhookEvent[] = [
  "approval.step_approved",
  "approval.completed",
  "approval.rejected",
];

export interface WebhookEndpoint {
  id: number;
  name: string;
  url: string;
  secret: string | null;
  events: string[];
  status: string;
  consecutive_failures: number | null;
  auto_paused_at: string | null;
  // 恢复后补投连续失败的次数；达到阈值后会被标记为「建议检查接收端」。
  replay_fail_streak: number | null;
  needs_attention: boolean | null;
  // 「标记已修复」前的降级状态快照；存在即表示可撤销，误标后一键还原。
  fix_snapshot: FixSnapshot | null;
  created_at: string;
  updated_at: string;
}

// 标记已修复前的降级状态快照，供撤销时忠实还原。
export interface FixSnapshot {
  streak?: number;
  needsAttention?: boolean;
  failures?: number;
}

export interface WebhookDelivery {
  id: number;
  endpoint_id: number | null;
  event: string;
  payload: Record<string, unknown> | null;
  status: string;
  attempts: number;
  max_attempts: number | null;
  next_retry_at: string | null;
  response_status: number | null;
  response_body: string | null;
  last_error: string | null;
  duration_ms: number | null;
  created_at: string;
  updated_at: string;
}

// 端点健康事件：自动暂停 / 手动恢复 / 连续失败清零 / 恢复前预检等。
export interface WebhookEndpointEvent {
  id: number;
  endpoint_id: number | null;
  endpoint_name: string | null;
  event_type: string;
  detail: string | null;
  actor_name: string | null;
  created_at: string;
}

export interface EndpointInput {
  endpointId?: number;
  name: string;
  url: string;
  secret: string;
  events: WebhookEvent[];
  enabled: boolean;
}

export interface DeliveryResult {
  ok: boolean;
  status: number | null;
  body: string | null;
  error: string | null;
}

// 恢复前预检结果：预检是否通过，恢复后自动补投了多少条真实待重试投递（成功多少条），
// 以及是否因「连续多次恢复后仍补投失败」触发降级（建议检查接收端）。
export interface ProbeResumeResult {
  result: DeliveryResult;
  replayed: boolean;
  replayCount: number;
  replaySuccess: number;
  degraded: boolean;
}

async function invokeWebhooks<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-webhooks", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseWebhooksResult {
  endpoints: WebhookEndpoint[];
  deliveries: WebhookDelivery[];
  events: WebhookEndpointEvent[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  saveEndpoint: (input: EndpointInput) => Promise<void>;
  deleteEndpoint: (endpointId: number) => Promise<void>;
  setStatus: (endpointId: number, status: "active" | "paused") => Promise<void>;
  probeResume: (endpointId: number) => Promise<ProbeResumeResult>;
  markFixed: (endpointId: number) => Promise<void>;
  undoMarkFixed: (endpointId: number) => Promise<void>;
  test: (endpointId: number, event: WebhookEvent) => Promise<DeliveryResult>;
  retry: (deliveryId: number) => Promise<DeliveryResult>;
  processRetries: () => Promise<{ processed: number; succeeded: number }>;
}

// 事件回调（Webhook）管理：端点订阅、测试投递、投递日志与重试，走 admin-webhooks。
export function useWebhooks(): UseWebhooksResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [events, setEvents] = useState<WebhookEndpointEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setEndpoints([]);
      setDeliveries([]);
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 进入回调页时先处理一次到期的自动重试（指数退避排期），再拉取最新数据。
      try {
        await invokeWebhooks({ action: "processRetries" });
      } catch {
        // 重试处理失败不阻塞列表加载
      }
      const data = await invokeWebhooks<{
        endpoints?: WebhookEndpoint[];
        deliveries?: WebhookDelivery[];
        events?: WebhookEndpointEvent[];
      }>({ action: "list" });
      setEndpoints(data.endpoints ?? []);
      setDeliveries(data.deliveries ?? []);
      setEvents(data.events ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "回调数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveEndpoint = useCallback(
    async (input: EndpointInput) => {
      await invokeWebhooks({ action: "saveEndpoint", ...input });
      await load();
    },
    [load],
  );

  const deleteEndpoint = useCallback(
    async (endpointId: number) => {
      await invokeWebhooks({ action: "deleteEndpoint", endpointId });
      await load();
    },
    [load],
  );

  const setStatus = useCallback(
    async (endpointId: number, status: "active" | "paused") => {
      await invokeWebhooks({ action: "setStatus", endpointId, status });
      await load();
    },
    [load],
  );

  // 恢复前预检：先发一条测试投递，通过才真正启用（避免刚恢复又立刻再次连败）；
  // 通过后会由后端自动补投该端点全部待重试的真实投递，一次性追回丢失的事件。
  const probeResume = useCallback(
    async (endpointId: number): Promise<ProbeResumeResult> => {
      const data = await invokeWebhooks<{
        result?: DeliveryResult;
        replayed?: boolean;
        replayCount?: number;
        replaySuccess?: number;
        degraded?: boolean;
      }>({ action: "probeResume", endpointId });
      await load();
      return {
        result: data.result ?? { ok: false, status: null, body: null, error: "unknown" },
        replayed: data.replayed ?? false,
        replayCount: data.replayCount ?? 0,
        replaySuccess: data.replaySuccess ?? 0,
        degraded: data.degraded ?? false,
      };
    },
    [load],
  );

  // 标记已修复：确认接收端已修复后，清除「建议检查接收端」降级标记与恢复后失败计数。
  const markFixed = useCallback(
    async (endpointId: number) => {
      await invokeWebhooks({ action: "markFixed", endpointId });
      await load();
    },
    [load],
  );

  // 撤销「标记已修复」：误标兜底，一键还原标记前的降级状态。
  const undoMarkFixed = useCallback(
    async (endpointId: number) => {
      await invokeWebhooks({ action: "undoMarkFixed", endpointId });
      await load();
    },
    [load],
  );

  const test = useCallback(
    async (endpointId: number, event: WebhookEvent) => {
      const result = await invokeWebhooks<{ result?: DeliveryResult }>({ action: "test", endpointId, event });
      await load();
      return result.result ?? { ok: false, status: null, body: null, error: "unknown" };
    },
    [load],
  );

  const retry = useCallback(
    async (deliveryId: number) => {
      const result = await invokeWebhooks<{ result?: DeliveryResult }>({ action: "retry", deliveryId });
      await load();
      return result.result ?? { ok: false, status: null, body: null, error: "unknown" };
    },
    [load],
  );

  const processRetries = useCallback(async () => {
    const result = await invokeWebhooks<{ processed?: number; succeeded?: number }>({ action: "processRetries" });
    await load();
    return { processed: result.processed ?? 0, succeeded: result.succeeded ?? 0 };
  }, [load]);

  return {
    endpoints,
    deliveries,
    events,
    loading: loading || authLoading,
    error,
    reload: load,
    saveEndpoint,
    deleteEndpoint,
    setStatus,
    probeResume,
    markFixed,
    undoMarkFixed,
    test,
    retry,
    processRetries,
  };
}